import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatChipsModule } from '@angular/material/chips';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil, catchError, finalize } from 'rxjs/operators';
import { HeaderNavComponent } from "src/app/ui/header-nav/header-nav.component";
import { FooterComponent } from "src/app/ui/footer/footer.component";
import { OrderService } from '../../api/services/order.service';
import { PaymentService } from '../../api/services/payment.service';
import { AnalyticsService, SalesAnalytics } from '../../api/services/analytics.service';
import { Order } from '../../api/models/order.model';
import { Page } from '../../api/models';
import { OrderDetailDialogComponent } from './order-detail-dialog/order-detail-dialog.component';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-staff-dashboard',
  standalone: true,
  imports: [
    CommonModule, MatCardModule, MatButtonModule, MatIconModule, MatTableModule,
    MatDialogModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatTabsModule, MatBadgeModule, MatSnackBarModule, MatToolbarModule,
    MatChipsModule, MatPaginatorModule, MatSortModule, MatProgressSpinnerModule,
    FormsModule, ReactiveFormsModule,
    HeaderNavComponent,
    FooterComponent
  ],
  templateUrl: './staff-dashboard.component.html',
  styles: []
})
export class StaffDashboardComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // Data sources
  dataSource = new MatTableDataSource<Order>();
  orders: Order[] = [];
  displayedColumns: string[] = ['id', 'table', 'customer', 'items', 'total', 'status', 'time', 'actions'];
  selectedTabIndex = 0;

  // Statistics from API
  salesAnalytics: SalesAnalytics | null = null;
  totalOrders = 0;
  pendingOrdersCount = 0;
  todaysRevenue = 0;
  averageOrderValue = 0;

  // Pagination
  totalElements = 0;
  pageSize = 20;
  currentPage = 0;

  // Overdue orders
  overdueOrders: Order[] = [];

  // Search
  searchQuery = '';
  searchStatus = '';
  searchTableNumber = '';

  // Loading states
  isLoading = false;
  isUpdatingStatus = false;

  private destroy$ = new Subject<void>();

  constructor(
    private orderService: OrderService,
    private paymentService: PaymentService,
    private analyticsService: AnalyticsService,
    private snackBar: MatSnackBar,
    private router: Router,
    private dialog: MatDialog
  ) {}


  /** True while a cash order is still waiting for someone to take the money. */
  /**
   * The payment record for each order on screen, by order id.
   *
   * The board only ever had the ORDER's own view of payment — method and a
   * PAID/UNPAID flag — while confirming acts on the PAYMENT record. The two
   * disagree: an order can read CASH/UNPAID while its payment is FAILED, or
   * while no payment row exists at all. Every one of the 28 "Cash received"
   * buttons on a real board was in that state, and pressing them did nothing.
   */
  paymentByOrder = new Map<number, any>();

  private loadPaymentsForOrders(): void {
    // One call for the page, rather than one per row.
    this.paymentService.getPayments(0, 200, undefined, 'id,desc').pipe(takeUntil(this.destroy$)).subscribe({
      next: (payments: any[]) => {
        const map = new Map<number, any>();
        for (const p of payments || []) {
          const key = Number(p.orderId);
          if (!Number.isFinite(key)) continue;
          // Keep the most recent attempt per order: that is the one whose
          // outcome the counter needs to see.
          const seen = map.get(key);
          const when = (x: any) => new Date(x?.updatedAt || x?.processedAt || x?.createdAt || 0).getTime();
          if (!seen || when(p) >= when(seen)) map.set(key, p);
        }
        this.paymentByOrder = map;
      },
      error: () => { /* the board still works without payment detail */ },
    });
  }

  paymentFor(order: any): any {
    return this.paymentByOrder.get(Number(order?.id));
  }

  /**
   * Which method the customer actually chose.
   *
   * NOT order.paymentMethod: the customer service stamps every order it
   * creates with "CASH" before the method has even been picked, because the
   * choice happens after the order is placed. So a card order's row claims
   * cash, and keying the button off it offered "Cash received" on card
   * orders — the opposite of what it is for. The payment record carries the
   * method the customer chose; the order's field is only a fallback for rows
   * with no payment at all.
   */
  private methodOf(order: any): string {
    const p = this.paymentFor(order);
    return String(p?.paymentMethod || order?.paymentMethod || '').toUpperCase();
  }

  /** Cash takers only, and only while the payment is genuinely awaiting them. */
  awaitingCash(order: any): boolean {
    if (String(order?.paymentState || 'UNPAID').toUpperCase() === 'PAID') return false;
    const p = this.paymentFor(order);
    // No record yet: the payment row may not have been written. Offering the
    // button would only produce "no cash payment is awaiting confirmation".
    if (!p) return false;
    if (String(p.paymentMethod || '').toUpperCase() !== 'CASH') return false;
    return String(p.status || '').toUpperCase() === 'PENDING';
  }

  /** Settled: show what was paid and how, never a button. */
  isPaid(order: any): boolean {
    const p = this.paymentFor(order);
    return String(order?.paymentState || '').toUpperCase() === 'PAID'
        || String(p?.status || '').toUpperCase() === 'COMPLETED';
  }

  paidLabel(order: any): string {
    const p = this.paymentFor(order);
    const method = this.methodOf(order) || 'PAID';
    const when = p?.processedAt || p?.updatedAt;
    return when ? `Paid · ${method} · ${new Date(when).toLocaleString()}` : `Paid · ${method}`;
  }

  /** A failed attempt: say what went wrong and when, rather than a dead button. */
  paymentFailed(order: any): boolean {
    return String(this.paymentFor(order)?.status || '').toUpperCase() === 'FAILED';
  }

  failureLabel(order: any): string {
    const p = this.paymentFor(order);
    const why = p?.gatewayResponse || p?.notes || 'Payment failed';
    const when = p?.updatedAt || p?.processedAt;
    return when ? `${why} · ${new Date(when).toLocaleString()}` : why;
  }

  /** Cash is owed but nothing recorded it — the counter cannot settle it here. */
  cashUnrecorded(order: any): boolean {
    return this.methodOf(order) === 'CASH'
        && !this.isPaid(order)
        && !this.paymentFor(order);
  }

  /**
   * Record that the cash actually arrived.
   *
   * Choosing "pay at counter" used to mark the order paid on the spot, so a
   * customer could settle their own bill and leave. The payment is now PENDING
   * until this is pressed by someone at the bar.
   */
  confirmCashReceived(order: any): void {
    if (!order?.id) return;
    this.isUpdatingStatus = true;
    this.paymentService.getPaymentsByOrder(order.id).subscribe({
      next: (payments) => {
        const cash = (payments || []).find((p: any) =>
          String(p.paymentMethod || '').toUpperCase() === 'CASH'
          && String(p.status || '').toUpperCase() !== 'COMPLETED');
        if (!cash?.id) {
          this.isUpdatingStatus = false;
          this.snackBar.open('No cash payment is awaiting confirmation for this order.', 'Close', { duration: 4000 });
          return;
        }
        this.paymentService.confirmCashReceived(cash.id).subscribe({
          next: () => {
            this.isUpdatingStatus = false;
            this.snackBar.open(`Cash received for order #${order.id}.`, 'Close', { duration: 3000 });
            this.refreshOrders();
          },
          error: (e) => {
            this.isUpdatingStatus = false;
            this.snackBar.open(e?.error?.error || 'Could not confirm the cash payment.', 'Close', { duration: 5000 });
          },
        });
      },
      error: () => {
        this.isUpdatingStatus = false;
        this.snackBar.open('Could not load the payment for this order.', 'Close', { duration: 4000 });
      },
    });
  }

  ngOnInit(): void {
    this.loadData();
    this.loadOverdueOrders();
  }

  loadOverdueOrders(): void {
    this.orderService.getOverdueOrders().pipe(takeUntil(this.destroy$)).subscribe({
      next: (orders) => this.overdueOrders = orders,
      error: () => this.snackBar.open('Failed to load overdue orders', 'Dismiss', { duration: 3000 })
    });
  }

  searchOrders(): void {
    if (!this.searchQuery && !this.searchStatus && !this.searchTableNumber) {
      this.dataSource.data = this.orders;
      return;
    }
    this.orderService.searchOrders({
      invoiceNumber: this.searchQuery || undefined,
      status: this.searchStatus || undefined,
      tableNumber: this.searchTableNumber || undefined,
      page: 0,
      size: this.pageSize
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (page: Page<Order>) => {
        this.dataSource.data = page.content || [];
        this.totalElements = page.totalElements || 0;
      },
      error: () => this.snackBar.open('Search failed', 'Close', { duration: 3000 })
    });
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.searchStatus = '';
    this.searchTableNumber = '';
    this.dataSource.data = this.orders;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  loadData(): void {
    this.isLoading = true;
    
    // Get today's date for analytics
    const today = new Date();
    const startDate = this.formatDate(today);
    const endDate = this.formatDate(today);

    forkJoin({
      orders: this.orderService.getAllOrders().pipe(
        catchError(error => {
          if (!environment.production) {
            console.error('Error loading orders:', error);
          }
          this.snackBar.open('Failed to load orders', 'Close', { duration: 3000 });
          return of([]);
        })
      ),
      analytics: this.analyticsService.getSalesAnalytics(startDate, endDate).pipe(
        catchError(error => {
          if (!environment.production) {
            console.error('Error loading analytics:', error);
          }
          this.snackBar.open('Could not load analytics data', 'Dismiss', { duration: 3000 });
          return of(null);
        })
      )
    })
    .pipe(
      takeUntil(this.destroy$),
      finalize(() => this.isLoading = false)
    )
    .subscribe(({ orders, analytics }) => {
      // Newest first. The server returns them in insertion order, and the
      // table paginates at 20, so on a real board the orders staff actually
      // have to act on sat on the last page while page 1 showed orders
      // delivered days ago.
      const when = (o: any) => new Date(o?.createdAt || 0).getTime() || Number(o?.id) || 0;
      const newestFirst = [...(orders || [])].sort((a, b) => when(b) - when(a));
      this.orders = newestFirst;
      this.dataSource.data = newestFirst;
      this.totalElements = newestFirst.length;
      // The payment record per row, which decides whether "Cash received" is
      // offered. This used to be called only from loadOrders(), which THIS
      // board never calls — it loads through loadData() — so the map stayed
      // empty and every cash order read "no payment recorded", button and all.
      this.loadPaymentsForOrders();
      
      // Calculate statistics
      this.totalOrders = orders.length;
      this.pendingOrdersCount = orders.filter(o => 
        o.status?.toUpperCase() === 'PENDING'
      ).length;
      
      if (analytics) {
        this.salesAnalytics = analytics;
        this.todaysRevenue = analytics.totalSales || 0;
        this.averageOrderValue = analytics.averageOrderValue || 0;
      } else {
        // Fallback: calculate from orders if analytics API fails
        this.todaysRevenue = orders.reduce((sum, order) => sum + (order.total || 0), 0);
        this.averageOrderValue = orders.length > 0 ? this.todaysRevenue / orders.length : 0;
      }
    });
  }

  onBack(): void {
    this.router.navigate(['/service-provider']);
  }

  onTabChange(event: any): void {
    this.selectedTabIndex = event.index;
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadOrders();
  }

  loadOrders(): void {
    this.isLoading = true;
    this.orderService.getOrders(this.currentPage, this.pageSize)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (response) => {
          this.orders = response.content || [];
          this.dataSource.data = this.orders;
          this.totalElements = response.totalElements || 0;
          this.loadPaymentsForOrders();
        },
        error: (error) => {
          if (!environment.production) {
            console.error('Error loading orders:', error);
          }
          this.snackBar.open('Failed to load orders', 'Close', { duration: 3000 });
        }
      });
  }

  // Statistics methods
  getTotalOrders(): number {
    return this.totalOrders;
  }

  getPendingOrders(): number {
    return this.pendingOrdersCount;
  }

  getTodaysRevenue(): number {
    return Math.round(this.todaysRevenue * 100) / 100;
  }

  getAverageOrderValue(): number {
    return Math.round(this.averageOrderValue * 100) / 100;
  }

  getOrdersByStatus(status: string): Order[] {
    return this.orders.filter(order => 
      order.status?.toUpperCase() === status.toUpperCase()
    );
  }

  getStatusColor(status: string): string {
    const normalizedStatus = status?.toUpperCase() || '';
    switch(normalizedStatus) {
      case 'PENDING': return 'warn';
      case 'CONFIRMED': 
      case 'PREPARING': return 'accent';
      case 'READY': 
      case 'DELIVERED': return 'primary';
      case 'CANCELLED': return 'warn';
      default: return 'primary';
    }
  }

  getStatusIcon(status: string): string {
    const normalizedStatus = status?.toUpperCase() || '';
    switch(normalizedStatus) {
      case 'PENDING': return 'warning';
      case 'CONFIRMED': return 'check';
      case 'PREPARING': return 'schedule';
      case 'READY': return 'check_circle';
      case 'DELIVERED': return 'local_shipping';
      case 'CANCELLED': return 'cancel';
      default: return 'help';
    }
  }

  getDisplayStatus(status: string): string {
    const normalizedStatus = status?.toUpperCase() || '';
    return normalizedStatus.charAt(0) + normalizedStatus.slice(1).toLowerCase();
  }

  // Action methods
  refreshOrders(): void {
    this.loadData();
    this.snackBar.open('Orders refreshed', 'Close', { duration: 2000 });
  }

  updateOrderStatus(order: Order, newStatus: string): void {
    if (!order.id) {
      this.snackBar.open('Invalid order ID', 'Close', { duration: 3000 });
      return;
    }

    this.isUpdatingStatus = true;
    const apiStatus = newStatus.toUpperCase();

    if (!environment.production) {
      console.log(`Updating order ${order.id} status to ${apiStatus}`);
    }

    this.orderService.updateOrderStatus(order.id, apiStatus)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isUpdatingStatus = false)
      )
      .subscribe({
        next: (updatedOrder) => {
          if (!environment.production) {
            console.log('Order status updated successfully:', updatedOrder);
          }
          
          // Update local data
          const index = this.orders.findIndex(o => o.id === order.id);
          if (index !== -1) {
            this.orders[index] = { ...this.orders[index], status: apiStatus };
            this.dataSource.data = [...this.orders];
          }
          
          // Recalculate counts
          this.updateOrderCounts();

          const statusMessages: { [key: string]: string } = {
            // CONFIRMED: 'Order has been confirmed',
            PREPARING: 'Order is now being prepared',
            READY: 'Order is ready for pickup',
            DELIVERED: 'Order has been delivered',
            CANCELLED: 'Order has been cancelled'
          };
          
          this.snackBar.open(statusMessages[apiStatus] || 'Order status updated', 'Close', { duration: 3000 });
        },
        error: (error) => {
          if (!environment.production) {
            console.error('Error updating order status:', error);
          }
          const errorMessage = error.error?.message || error.message || 'Failed to update order status';
          this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        }
      });
  }

  cancelOrder(order: Order): void {
    if (!order.id) {
      this.snackBar.open('Invalid order ID', 'Close', { duration: 3000 });
      return;
    }

    this.orderService.updateOrderStatus(order.id, 'CANCELLED')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const index = this.orders.findIndex(o => o.id === order.id);
          if (index !== -1) {
            this.orders[index] = { ...this.orders[index], status: 'CANCELLED' };
            this.dataSource.data = [...this.orders];
          }
          this.updateOrderCounts();
          this.snackBar.open('Order cancelled successfully', 'Close', { duration: 3000 });
        },
        error: (error) => {
          if (!environment.production) {
            console.error('Error cancelling order:', error);
          }
          this.snackBar.open('Failed to cancel order', 'Close', { duration: 3000 });
        }
      });
  }

  openOrderDetail(order: Order): void {
    const dialogRef = this.dialog.open(OrderDetailDialogComponent, {
      data: { order },
      width: '500px',
      maxWidth: '90vw',
      panelClass: 'order-detail-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'updateStatus') {
        // Determine next status
        const currentStatus = result.order.status?.toUpperCase();
        let nextStatus = '';
        
        switch (currentStatus) {
          case 'PENDING':
            nextStatus = 'PREPARING';
            break;
          case 'PREPARING':
            nextStatus = 'READY';
            break;
          case 'READY':
            nextStatus = 'DELIVERED';
            break;
        }
        
        if (nextStatus) {
          this.updateOrderStatus(result.order, nextStatus);
        }
      }
    });
  }

  getItemsCount(order: Order): number {
    return order.items?.length || 0;
  }

  getItemsSummary(order: Order): string {
    if (!order.items || order.items.length === 0) {
      return 'No items';
    }
    return order.items.map(item => `${item.quantity}x ${item.drinkName || 'Item'}`).join(', ');
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private updateOrderCounts(): void {
    this.totalOrders = this.orders.length;
    this.pendingOrdersCount = this.orders.filter(o => 
      o.status?.toUpperCase() === 'PENDING'
    ).length;
  }
}